// BatchDetail — batch metadata card + child-row table with 30s live polling.
//
// Route: /payouts/batches/:batchId (wired in router.tsx under RequireAuth → App).
// Data: useBatch(batchId) polls at 30s; stops when all child rows are terminal.
//
// Column layout mirrors Payouts.tsx — Request ID · Amount · UTR/Txn · Created · Status —
// with a leading # column (1-based, derived from array insertion order).
//
// CLAUDE.md rule 6: amount_minor arrives as bigint from useBatch; passed to
// fmtCcyMinor for display. No float coercions.
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { I } from "@/components/Icons";
import { Modal, Segmented, StatusPill, EmptyState } from "@/components/UI";
import { FailureReason } from "@/components/FailureReason";
import { fmtCcyMinor, formatDateTime } from "@/utils/format";
import { downloadCsv } from "@/utils/csv";
import { useBatch } from "@/queries/useBatch";
import { useQueryClient } from "@tanstack/react-query";
import type { Payout } from "@/api/payouts";
import { approveBatch, rejectBatch, type BatchIngestState } from "@/api/batches";
import { cancelBatch, type CancelBatchResult } from "@/api/payouts";
import { useAuth, isAdminRole } from "@/store/auth";
import { useUI } from "@/store/ui";
import { apiErrorMessage } from "@/utils/apiError";
import { type FormEvent } from "react";

// ---------------------------------------------------------------------------
// Filter type
// ---------------------------------------------------------------------------

type RowFilter = "all" | "pending" | "success" | "failure";

const FILTERS: { value: RowFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "success", label: "Success" },
  { value: "failure", label: "Failure" },
];

// ---------------------------------------------------------------------------
// Live indicator — "Updated N s ago · live"
// ---------------------------------------------------------------------------

function LiveIndicator({ lastFetchedAt }: { lastFetchedAt: number }) {
  const [secsAgo, setSecsAgo] = useState(0);

  useEffect(() => {
    setSecsAgo(0);
    const id = setInterval(() => {
      setSecsAgo(Math.round((Date.now() - lastFetchedAt) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [lastFetchedAt]);

  return (
    <span
      style={{
        fontSize: "var(--type-xs)",
        color: "var(--ink-4)",
      }}
    >
      Updated {secsAgo}s ago · live
    </span>
  );
}

// ---------------------------------------------------------------------------
// BatchDetail page
// ---------------------------------------------------------------------------

export function BatchDetail() {
  const { batchId: batchIdParam } = useParams<{ batchId: string }>();
  // AUDIT-PASS-8 panel M-4: pre-fix, Number(batchIdParam ?? "0") returned
  // NaN for non-numeric URLs (/payouts/batches/abc) and 0 for missing
  // params. NaN was then sent to useBatch → getBatch → URL
  // /v1/merchants/X/payouts/batches/NaN, which the backend rejected as
  // 400/404. The user saw "Could not load batch" — a confusing error for
  // what's actually a URL typo. Strict /^\d+$/ + parseInt gives us a clear
  // null sentinel for invalid IDs; useBatch is gated to skip the fetch
  // and the page renders an unambiguous "Invalid batch ID" state below.
  const batchId =
    batchIdParam != null && /^\d+$/.test(batchIdParam)
      ? parseInt(batchIdParam, 10)
      : null;

  const [filter, setFilter] = useState<RowFilter>("all");
  const [lastFetchedAt, setLastFetchedAt] = useState<number>(Date.now());

  const { data, isLoading, isError, dataUpdatedAt, refetch } = useBatch(
    batchId ?? 0,
  );

  // Track last successful fetch timestamp for the live indicator
  useEffect(() => {
    if (dataUpdatedAt > 0) {
      setLastFetchedAt(dataUpdatedAt);
    }
  }, [dataUpdatedAt]);

  // Filter child rows client-side
  const allPayouts: Payout[] = data?.payouts ?? [];
  const rows =
    filter === "all" ? allPayouts : allPayouts.filter((p) => p.status === filter);

  // Counters for the queue-progress strip. Computed off the full set
  // (not the filtered view) so the numbers stay stable when the operator
  // flips between segmented filters.
  const counts = {
    success: allPayouts.filter((p) => p.status === "success").length,
    failure: allPayouts.filter((p) => p.status === "failure").length,
    pending: allPayouts.filter((p) => p.status === "pending").length,
    needsApproval: allPayouts.filter((p) => p.status === "needs_approval").length,
    // Whether a row can be cancelled is the BACKEND's answer (can_cancel),
    // never re-derived here — the rule turns on the rail type and on whether
    // an export has claimed the row. Counting it this way means the button's
    // number is exactly what the endpoint will act on.
    cancellable: allPayouts.filter((p) => p.can_cancel === true).length,
  };
  const total = allPayouts.length;
  const settled = counts.success + counts.failure;
  const pct = total > 0 ? Math.round((settled / total) * 100) : 0;

  // Bulk-approval state. The page is reachable by any merchant member who
  // can see the batch; only admins can act on the bulk endpoints. Buttons
  // hide entirely for non-admins so we don't tease an unusable affordance.
  const isAdmin = useAuth((s) => isAdminRole(s.user?.role));
  const navigate = useNavigate();
  const addToast = useUI((s) => s.addToast);
  const qc = useQueryClient();
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const showBulkButtons = isAdmin && counts.needsApproval > 0;
  // Cancel is offered independently of the approve/reject pair. A batch whose
  // rows have all been approved onto a FILE rail has needsApproval = 0 and is
  // still entirely cancellable — that is precisely the batch an operator most
  // wants to stop, and gating it behind the approval buttons would hide it.
  const [bulkCancelOpen, setBulkCancelOpen] = useState(false);
  const showCancelButton = isAdmin && counts.cancellable > 0;

  async function handleBulkApprove() {
    if (batchId == null) return;
    setBulkBusy(true);
    try {
      const res = await approveBatch(batchId);
      addToast({
        title: `Approved ${res.approved_count} rows`,
        body: [
          res.already_handled_ids.length > 0
            ? `${res.already_handled_ids.length} already handled by another admin`
            : "",
          res.dispatch_errors > 0
            ? `${res.dispatch_errors} dispatch errors — reconciler will resolve`
            : "",
        ]
          .filter(Boolean)
          .join(" · "),
        variant: "success",
      });
      void qc.invalidateQueries({ queryKey: ["batch", batchId] });
      void qc.invalidateQueries({ queryKey: ["needs-approval"] });
    } catch (err) {
      addToast({
        title: "Bulk approve failed",
        body: apiErrorMessage(
          err,
          // Pre-fix wording was "The batch did not approve" which read like
          // the BANK rejected. This is OUR approve-API call failing — the
          // bank hasn't been consulted yet at this point. Reworded to be
          // explicit about whose decision this is.
          "Could not submit the approval. Refresh and try again, or approve rows individually from /approvals.",
        ),
        variant: "error",
      });
    } finally {
      setBulkBusy(false);
    }
  }

  // ── Invalid-URL state (AUDIT-PASS-8 M-4) ─────────────────────────────────
  // batchId is null when the URL param failed /^\d+$/. useBatch is gated
  // on batchId > 0 so no fetch happens; render an unambiguous message
  // instead of the generic "Could not load batch" that pre-fix appeared
  // when the backend rejected /batches/NaN as 400/404.
  if (batchId == null) {
    return (
      <div className="page">
        <div className="page-head">
          <div>
            <div className="eyebrow">Console</div>
            <h1>Batch</h1>
          </div>
        </div>
        <EmptyState
          icon={<I.X size={16} />}
          title="Invalid batch ID"
          body="The batch ID in the URL is not a valid number. Check the link or go back to Payouts."
        />
      </div>
    );
  }

  // ── Error state ──────────────────────────────────────────────────────────
  if (isError) {
    return (
      <div className="page">
        <div className="page-head">
          <div>
            <div className="eyebrow">Console</div>
            <h1>Batch #{batchId}</h1>
          </div>
        </div>
        <EmptyState
          icon={<I.X size={16} />}
          title="Could not load batch"
          body="There was a problem fetching this batch. Refresh the page or go back to Payouts."
          action={
            // AUDIT-PASS-6 P-1: was window.location.reload() — a full
            // page reload tears down the React app, drops the auth
            // store's in-memory access token, refires every query, and
            // bounces the operator out of the route. The query failed,
            // not the app; refetch() retries just this query in place.
            <button
              className="btn ghost sm"
              type="button"
              onClick={() => void refetch()}
            >
              Reload
            </button>
          }
        />
      </div>
    );
  }

  const batch = data?.batch;
  const submittedBy = batch?.submitted_by != null ? `User #${batch.submitted_by}` : "API";

  return (
    <div className="page">
      {/* PDF round 3 N2 fix — background-ingestion banner. Surfaced at the
          top of the page (above the regular page-head) while the backend
          worker is still registering beneficiaries server-side. The
          batch detail useBatch hook polls at 2s while phase==='registering'
          and reverts to the standard 30s once it flips to done/failed. */}
      {batch?.ingest_state && (
        <IngestStateBanner state={batch.ingest_state} />
      )}

      {/* ── Page head ─────────────────────────────────────────────────── */}
      <div className="page-head">
        <div>
          <div className="eyebrow">Console</div>
          <h1>Batch #{batchId}</h1>
          <div className="sub">
            {isLoading
              ? "Loading…"
              : batch
                ? `${batch.row_count} rows · submitted ${formatDateTime(batch.created_at)} · by ${submittedBy}`
                : ""}
          </div>
        </div>
        <div className="actions" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {showBulkButtons && (
            <>
              {/* Admin-actions affordance label. Pre-fix it was easy
                  to mistake this view for "what the merchant sees"
                  when an admin used the merchant switcher to inspect
                  a batch. The label + accent-coloured pill keeps the
                  cross-tenant power obvious without nagging.*/}
              <span
                className="eyebrow"
                style={{
                  fontSize: 10,
                  letterSpacing: "0.06em",
                  padding: "3px 8px",
                  borderRadius: 999,
                  background: "var(--accent-soft, rgba(0, 102, 255, 0.08))",
                  color: "var(--accent, #0066ff)",
                  whiteSpace: "nowrap",
                }}
                title="These buttons are only visible to admins. They act across every needs_approval row in this batch."
              >
                Admin actions
              </span>
              <button
                className="btn ghost sm"
                type="button"
                onClick={() => setBulkRejectOpen(true)}
                disabled={bulkBusy}
              >
                <I.X size={11} /> Reject all ({counts.needsApproval})
              </button>
              <button
                className="btn accent sm"
                type="button"
                onClick={handleBulkApprove}
                disabled={bulkBusy}
                title="Approve every needs_approval row in this batch and dispatch to the provider. May take a few minutes for large batches."
              >
                {bulkBusy ? (
                  "Approving…"
                ) : (
                  <>
                    <I.Check size={11} /> Approve all ({counts.needsApproval})
                  </>
                )}
              </button>
            </>
          )}
          {/* Cancel all — outside the showBulkButtons gate on purpose. A batch
              already approved onto a file rail has zero needs_approval rows and
              is still fully cancellable, and that is exactly the batch someone
              is trying to stop. */}
          {showCancelButton && (
            <button
              className="btn danger sm"
              type="button"
              onClick={() => setBulkCancelOpen(true)}
              disabled={bulkBusy}
              title="Stop every row in this batch that has not left the system."
            >
              <I.X size={11} /> Cancel all ({counts.cancellable})
            </button>
          )}
          <button
            className="btn ghost sm"
            type="button"
            onClick={() =>
              downloadCsv(
                `batch-${batchId}-rows.csv`,
                rows.map((p) => ({
                  id: p.id,
                  request_id: p.request_id,
                  status: p.status,
                  amount_minor: p.amount_minor,
                  currency: p.currency,
                  txn_mode: p.txn_mode,
                  provider_txn_id: p.provider_txn_id,
                  utr_no: p.utr_no,
                  // Failure-reason fields included in the export so
                  // operators triaging a failed batch in a spreadsheet
                  // have the code + message alongside the row, not just
                  // 'Failure' with no context (audit pass-3 CSV
                  // visibility gap).
                  error_code: p.error_code,
                  error_message: p.error_message,
                  created_at: p.created_at,
                })),
              )
            }
          >
            <I.Download size={12} /> Export rows
          </button>
        </div>
      </div>

      {/* ── Queue progress strip ───────────────────────────────────────
          At-a-glance view of where the batch is. The 3-segment bar shows
          success (var(--ok)), failure (var(--warn)), pending (--hair)
          proportionally; the counters underneath give exact numbers and
          the percentage is "settled / total" — both terminal states count
          as settled because the batch dispatch loop is no longer touching
          those rows. */}
      {!isLoading && total > 0 && (
        <div className="card" style={{ marginTop: "var(--card-gap)" }}>
          <div className="card-body">
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 12,
                marginBottom: 8,
              }}
            >
              <strong style={{ fontSize: 14 }}>{pct}% settled</strong>
              <span className="dim" style={{ fontSize: 12 }}>
                {settled} of {total} rows{counts.pending > 0 ? ` · ${counts.pending} still in flight` : ""}
              </span>
              <div className="flex-1" />
              <span style={{ fontSize: 12, color: "var(--ok, #1a7f37)" }}>
                ✓ {counts.success} success
              </span>
              <span style={{ fontSize: 12, color: "var(--warn, #b42318)" }}>
                ✕ {counts.failure} failure
              </span>
              <span className="dim" style={{ fontSize: 12 }}>
                ⏳ {counts.pending} pending
              </span>
            </div>
            {/* Stacked 3-segment bar. Widths in % so the visual sums to 100
                regardless of total. Min-width tricks avoid the slivers
                disappearing entirely when a segment is tiny. */}
            <div
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${pct}% settled, ${counts.success} success, ${counts.failure} failure, ${counts.pending} pending`}
              style={{
                display: "flex",
                height: 8,
                background: "var(--hair)",
                borderRadius: 4,
                overflow: "hidden",
              }}
            >
              <span
                style={{
                  width: `${(counts.success / total) * 100}%`,
                  background: "var(--ok, #1a7f37)",
                  transition: "width 300ms ease",
                }}
              />
              <span
                style={{
                  width: `${(counts.failure / total) * 100}%`,
                  background: "var(--warn, #b42318)",
                  transition: "width 300ms ease",
                }}
              />
              {/* pending stays as the leftover track colour — no fill */}
            </div>
          </div>
        </div>
      )}

      {/* ── Batch metadata card ───────────────────────────────────────── */}
      <div className="card" aria-label={isLoading ? "Loading batch details" : undefined}>
        <div className="card-head">
          <h3>Batch summary</h3>
          <div className="spacer" />
          {batch && <StatusPill status={batch.status} />}
          {isLoading && (
            <span className="skel" style={{ width: 60, height: 18, display: "inline-block" }} />
          )}
        </div>
        <div className="card-body">
          <dl className="dl">
            <dt>Source account</dt>
            <dd>
              {isLoading ? (
                <span className="skel" style={{ width: 120, height: 13, display: "inline-block" }} />
              ) : (
                batch ? `Account #${batch.bank_account_id}` : "—"
              )}
            </dd>

            <dt>Total amount</dt>
            <dd className="mono">
              {isLoading ? (
                <span className="skel" style={{ width: 80, height: 13, display: "inline-block" }} />
              ) : (
                batch ? fmtCcyMinor(batch.total_amount_minor, "INR") : "—"
              )}
            </dd>

            <dt>Rows submitted</dt>
            <dd>
              {isLoading ? (
                <span className="skel" style={{ width: 30, height: 13, display: "inline-block" }} />
              ) : (
                batch?.row_count ?? "—"
              )}
            </dd>

            <dt>Submitted by</dt>
            <dd>
              {isLoading ? (
                <span className="skel" style={{ width: 100, height: 13, display: "inline-block" }} />
              ) : (
                submittedBy
              )}
            </dd>

            <dt>Submitted at</dt>
            <dd>
              {isLoading ? (
                <span className="skel" style={{ width: 110, height: 13, display: "inline-block" }} />
              ) : (
                batch ? formatDateTime(batch.created_at) : "—"
              )}
            </dd>

            <dt>Batch status</dt>
            <dd>
              {isLoading ? (
                <span className="skel" style={{ width: 70, height: 18, display: "inline-block" }} />
              ) : (
                batch ? <StatusPill status={batch.status} /> : "—"
              )}
            </dd>
          </dl>
        </div>
      </div>

      {/* ── Toolbar ───────────────────────────────────────────────────── */}
      <div className="toolbar">
        <Segmented value={filter} onChange={setFilter} options={FILTERS} />
        <div className="flex-1" />
        {!isLoading && <LiveIndicator lastFetchedAt={lastFetchedAt} />}
      </div>

      {/* ── Child rows table ─────────────────────────────────────────── */}
      <div className="card flat">
        {isLoading ? (
          <table className="tbl">
            <thead>
              <tr>
                <th className="cell-id">#</th>
                <th>Request ID</th>
                <th className="num">Amount</th>
                <th>UTR / Txn</th>
                <th>Created</th>
                <th>Status</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  <td><span className="skel" style={{ width: 18, height: 12, display: "inline-block" }} /></td>
                  <td><span className="skel" style={{ width: 120, height: 12, display: "inline-block" }} /></td>
                  <td><span className="skel" style={{ width: 70, height: 12, display: "inline-block" }} /></td>
                  <td><span className="skel" style={{ width: 80, height: 12, display: "inline-block" }} /></td>
                  <td><span className="skel" style={{ width: 90, height: 12, display: "inline-block" }} /></td>
                  <td><span className="skel" style={{ width: 56, height: 18, display: "inline-block" }} /></td>
                  <td><span className="skel" style={{ width: 110, height: 12, display: "inline-block" }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<I.CSV size={16} />}
            title="No rows in this batch"
            body={
              filter === "all"
                ? "This batch was submitted with no valid rows."
                : `No rows with status "${filter}" found.`
            }
          />
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th className="cell-id">#</th>
                <th>Request ID</th>
                <th className="num">Amount</th>
                <th>UTR / Txn</th>
                <th>Created</th>
                <th>Status</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p, idx) => (
                <tr
                  key={p.id}
                  // Admin-only drill-down — destination shows raw
                  // provider payload + audit trail. Merchants get a
                  // read-only row with status + reason only.
                  role={isAdmin ? "button" : undefined}
                  tabIndex={isAdmin ? 0 : undefined}
                  onClick={
                    isAdmin
                      ? () => navigate(`/payouts/${p.id}`)
                      : undefined
                  }
                  onKeyDown={
                    isAdmin
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            navigate(`/payouts/${p.id}`);
                          }
                        }
                      : undefined
                  }
                  style={isAdmin ? { cursor: "pointer" } : undefined}
                  title={isAdmin ? `Open pay_${p.id}` : undefined}
                >
                  <td className="cell-id mono">{idx + 1}</td>
                  <td className="mono">{p.request_id}</td>
                  <td className="num">
                    {fmtCcyMinor(p.amount_minor, p.currency)}
                  </td>
                  <td className="mono dim" style={{ fontSize: 11 }}>
                    {p.utr_no ?? p.provider_txn_id ?? "—"}
                  </td>
                  <td className="dim">{formatDateTime(p.created_at)}</td>
                  <td>
                    <StatusPill status={p.status} />
                  </td>
                  <td>
                    <FailureReason payout={p} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Bulk-reject modal — mounted only when the button was clicked AND
          the row exists. Identical contract to the Approvals page reject
          modal but applies to every needs_approval row in this batch. */}
      {bulkCancelOpen && batchId != null && (
        <BulkCancelModal
          batchId={batchId}
          rowCount={counts.cancellable}
          onClose={() => setBulkCancelOpen(false)}
          onCancelled={(res) => {
            addToast({
              title: `Cancelled ${res.cancelled_count} of ${counts.cancellable} rows`,
              // The blocked count is the operationally important half: it is
              // what is still live. Naming it here means an operator who reads
              // only the toast still knows the batch is not fully stopped.
              body:
                res.blocked_count > 0
                  ? `${res.blocked_count} could not be cancelled — they have already gone out. Open the rows to see why.`
                  : "Nothing in this batch is still live.",
              variant: res.blocked_count > 0 ? "error" : "success",
            });
            void qc.invalidateQueries({ queryKey: ["batch", batchId] });
            void qc.invalidateQueries({ queryKey: ["batch-payouts", batchId] });
            setBulkCancelOpen(false);
          }}
        />
      )}

      {bulkRejectOpen && batchId != null && (
        <BulkRejectModal
          batchId={batchId}
          rowCount={counts.needsApproval}
          open
          onClose={() => setBulkRejectOpen(false)}
          onRejected={(res) => {
            addToast({
              title: `Rejected ${res.rejected_count} rows`,
              body:
                res.already_handled_ids.length > 0
                  ? `${res.already_handled_ids.length} already handled by another admin`
                  : "All needs-approval rows in this batch are now marked failure.",
              variant: "success",
            });
            void qc.invalidateQueries({ queryKey: ["batch", batchId] });
            void qc.invalidateQueries({ queryKey: ["needs-approval"] });
            setBulkRejectOpen(false);
          }}
        />
      )}
    </div>
  );
}

// ─── Bulk reject modal ────────────────────────────────────────────────────────

function BulkRejectModal({
  batchId,
  rowCount,
  open,
  onClose,
  onRejected,
}: {
  batchId: number;
  rowCount: number;
  open: boolean;
  onClose: () => void;
  onRejected: (res: { rejected_count: number; already_handled_ids: number[] }) => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const MAX = 500;
  const trimmed = reason.trim();
  const canSubmit = !busy && trimmed.length > 0 && trimmed.length <= MAX;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const res = await rejectBatch(batchId, trimmed);
      onRejected(res);
    } catch (err) {
      setError(
        apiErrorMessage(
          err,
          "Could not reject the batch — rows may already be in flight.",
        ),
      );
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={busy}
      title={`Reject ${rowCount} rows?`}
      foot={
        <>
          <button
            className="btn ghost"
            type="button"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <div className="flex-1" />
          <button
            className="btn danger"
            type="submit"
            form="bulk-reject-form"
            disabled={!canSubmit}
          >
            {busy ? "Rejecting…" : `Reject ${rowCount} rows`}
          </button>
        </>
      }
    >
      <form
        id="bulk-reject-form"
        onSubmit={handleSubmit}
        className="stack-16"
      >
        <div className="dim" style={{ fontSize: 13, lineHeight: 1.5 }}>
          Every needs_approval row in batch{" "}
          <span className="mono">bat_{batchId}</span> will be marked{" "}
          <strong>failure</strong> with{" "}
          <span className="mono">error_code=admin_rejected</span> and the
          same reason. The audit log records one{" "}
          <span className="mono">payout.admin_reject</span> entry per row.
          No money moves.
        </div>
        <div className="field">
          <label className="label" htmlFor="bulk-reject-reason">
            Reason{" "}
            <span style={{ color: "var(--ink-3)" }}>
              · {trimmed.length}/{MAX}
            </span>
          </label>
          <textarea
            id="bulk-reject-reason"
            className="input"
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoFocus
            placeholder="e.g. CSV referenced wrong PO number. Operator to re-upload corrected file."
          />
        </div>
        {error && (
          <div style={{ color: "var(--warn, #b42318)", fontSize: 12 }}>
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// PDF round 3 N2 fix — background-ingestion progress banner
// ---------------------------------------------------------------------------
//
// Shown above the regular page head while the backend worker is still
// registering beneficiaries. useBatch polls at 2s while phase==='registering'
// and falls back to the standard 30s once the worker completes. Once phase
// flips to 'done' or 'failed' the banner stays visible (informational) but
// loses the spinner; if every row was failure we tint red.

function IngestStateBanner({ state }: { state: BatchIngestState }) {
  const pct = state.total > 0 ? Math.round((state.done / state.total) * 100) : 0;
  const inProgress = state.phase === "registering";
  const failed = state.phase === "failed" || state.failed === state.total;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        background: failed ? "var(--neg-soft)" : "var(--bg-2, #f7f7f8)",
        border: failed ? "1px solid var(--neg)" : "1px solid var(--hair)",
        borderRadius: 8,
        padding: "12px 16px",
        marginBottom: 16,
        display: "flex",
        alignItems: "center",
        gap: 14,
      }}
    >
      {inProgress ? (
        <div
          style={{
            width: 16,
            height: 16,
            border: "2px solid var(--hair)",
            borderTopColor: "var(--accent)",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
            flexShrink: 0,
          }}
        />
      ) : failed ? (
        <I.Banks size={16} stroke={2} style={{ color: "var(--neg)" }} />
      ) : (
        <I.Check size={16} stroke={2.5} style={{ color: "var(--pos)" }} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          {inProgress
            ? `Registering beneficiaries — ${state.done} of ${state.total} complete (${pct}%)`
            : failed
              ? `Beneficiary registration failed — ${state.failed} of ${state.total} rows could not register`
              : `Beneficiary registration complete — ${state.done} of ${state.total} rows registered`}
        </div>
        <div className="dim" style={{ fontSize: 11, marginTop: 2 }}>
          {inProgress
            ? "Server-side worker — rate-limit aware. Page polls every 2 seconds."
            : state.failed > 0
              ? `${state.failed} row${state.failed === 1 ? "" : "s"} failed registration and were marked as failed in the table below.`
              : "All beneficiaries registered. Rows are now in the approval queue."}
        </div>
        {state.error && (
          <div style={{ fontSize: 11, marginTop: 4, color: "var(--neg)" }}>
            {state.error}
          </div>
        )}
      </div>
      {inProgress && (
        <div
          style={{
            width: 120,
            height: 6,
            background: "var(--bg-sunk)",
            borderRadius: 3,
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: "100%",
              background: "var(--accent)",
              transition: "width 250ms ease-out",
            }}
          />
        </div>
      )}
    </div>
  );
}

// ─── Bulk cancel modal ────────────────────────────────────────────────────────
//
// Separate from BulkRejectModal rather than a mode on it. Reject declines rows
// awaiting review; cancel stops rows that were already approved but have not
// left the system. They act on different rows, read differently to an operator,
// and merging them behind a flag would make both harder to reason about at the
// moment somebody is trying to stop money.
function BulkCancelModal({
  batchId,
  rowCount,
  onClose,
  onCancelled,
}: {
  batchId: number;
  rowCount: number;
  onClose: () => void;
  onCancelled: (res: CancelBatchResult) => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const trimmed = reason.trim();
    if (trimmed === "") {
      setError("Give a reason — it is stored on every row and in the audit log.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onCancelled(await cancelBatch(batchId, trimmed));
    } catch (err) {
      setError(apiErrorMessage(err, "The batch could not be cancelled."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={`Cancel ${rowCount} row${rowCount === 1 ? "" : "s"} in batch #${batchId}?`}
      onClose={onClose}
      foot={
        <>
          <button className="btn ghost" type="button" onClick={onClose} disabled={busy}>
            Keep them
          </button>
          <button className="btn danger" type="button" onClick={() => void submit()} disabled={busy}>
            {busy ? "Cancelling…" : `Cancel ${rowCount} row${rowCount === 1 ? "" : "s"}`}
          </button>
        </>
      }
    >
      <div className="stack-12">
        <p style={{ margin: 0, fontSize: 13 }}>
          Every row that has not left the system will be stopped and marked
          failed. Rows already sent to the payout rail, or already claimed by a
          payment file, are left alone — they cannot be stopped from here.
        </p>
        <p className="dim" style={{ margin: 0, fontSize: 12 }}>
          No money has moved for the rows being cancelled, so nothing needs
          recovering. This cannot be undone.
        </p>
        <div className="field">
          <label className="label" htmlFor="bulk-cancel-reason">
            Reason
          </label>
          <input
            id="bulk-cancel-reason"
            className="input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Stored on every cancelled row"
            maxLength={500}
            autoFocus
            autoComplete="off"
          />
        </div>
        {error && (
          <p className="hint neg" style={{ fontSize: 12 }}>
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
